import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

import { withSuspense } from '@/components/hocs/suspense/with-suspense';
import { ROUTES, toRoutePath } from '@/constants/routes';
import { retryChunkLoad } from '@/utils/chunk-error-handler';
import { isRouteAccessibleInCurrentEnvironment } from '@/utils/prelaunch-route-access';

// Lazy load dashboard pages with chunk error handling
const DashboardPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/dashboard/analytics-page'))
  )
);

const DashboardActivatePage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/analytics-page/dashboard-activate-page')
    )
  )
);

const AIAgentsDashboardPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/dashboard/ai-agents-page'))
  )
);

const AgentSuiteDetailPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/ai-agents-page/agent-suite-detail-page')
    )
  )
);

const AgentActivationPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () => import('../../pages/dashboard/ai-agents-page/agent-activation-page')
    )
  )
);

const CompanyInfoSettings = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () => import('../../pages/dashboard/settings-page/company-info-settings')
    )
  )
);

const DepartmentInfoSettings = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/settings-page/department-info-settings')
    )
  )
);

const BusinessStackDashboardPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/dashboard/business-stack-page'))
  )
);

const BusinessStackSelectAgentPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/business-stack-page/select-agent-page')
    )
  )
);

const BusinessStackActivateSuitePage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/business-stack-page/activate-suite-page')
    )
  )
);

const SalesforceHelpPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/business-stack-page/salesforce-help-page')
    )
  )
);

const KnowledgeBaseDashboardPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/dashboard/knowledge-base-page'))
  )
);

const KnowledgeBaseSelectAgentPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/knowledge-base-page/select-agent-page')
    )
  )
);

const KnowledgeBaseActivationPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/knowledge-base-page/knowledge-base-activation-page')
    )
  )
);

const PolicyBooksPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/dashboard/policy-books-page'))
  )
);

const PolicyBookDetailsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/policy-books-page/policy-book-details-page')
    )
  )
);

const PolicyBookAuditLogPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/policy-books-page/policy-book-audit-log-page')
    )
  )
);

const PolicyBookPendingApprovalPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/policy-books-page/policy-book-pending-approval-page')
    )
  )
);

const PolicyBookDraftsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/policy-books-page/policy-book-drafts-page')
    )
  )
);

const PolicyBookCommentsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/policy-books-page/policy-book-comments-page')
    )
  )
);

const AgenticTickerPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/dashboard/agentic-ticker-page'))
  )
);

const SettingsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/dashboard/settings-page'))
  )
);

const MembersSettings = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () => import('../../pages/dashboard/settings-page/members-settings')
    )
  )
);

const InviteMembersPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/settings-page/members-settings/invite-members-page')
    )
  )
);

const RequestToJoinPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/settings-page/members-settings/request-to-join-page')
    )
  )
);

// New Analytics Dashboard Components
const AnalyticsDashboard = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () => import('../../pages/dashboard/analytics-page/analytics')
    )
  )
);

const InsightsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/analytics-page/analytics/insights-page')
    )
  )
);

const TaskLogsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/analytics-page/analytics/task-logs-page')
    )
  )
);

const AssignmentLogsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/analytics-page/analytics/assignment-logs-page')
    )
  )
);

const AssignmentLogDetailsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/analytics-page/analytics/assignment-logs-page/assignment-log-details-page')
    )
  )
);

const AllInsightsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/analytics-page/analytics/insights-page/all-insights-page')
    )
  )
);

const TaskLogDetailsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/analytics-page/analytics/task-logs-page/task-log-details-page')
    )
  )
);

const AnalyticsSuiteDetailsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/analytics-page/analytics/suite-details-page')
    )
  )
);

const ScheduleHeartbeatPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () =>
        import('../../pages/dashboard/analytics-page/analytics/schedule-heartbeat-page')
    )
  )
);

const NotFoundPage = withSuspense(
  lazy(() => retryChunkLoad(() => import('../../pages/not-found-page')))
);

const DashboardChatLayout = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () => import('../../components/layout/dashboard-chat-layout')
    )
  )
);

const ChinobiVideoChat = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/dashboard/video-chat/chinobi'))
  )
);

const dashboardRoutePath = (
  route: string,
  parentRoute: string = ROUTES.DASHBOARD_BASE
) => toRoutePath(route, parentRoute);

export const DashboardRoutes = [
  {
    index: true,
    element: <Navigate to={ROUTES.DASHBOARD_AI_AGENTS} replace />,
  },
  {
    path: dashboardRoutePath(ROUTES.DASHBOARD_VIDEO_CHAT_CHINOBI),
    element: <ChinobiVideoChat />,
  },
  {
    element: <DashboardChatLayout />,
    children: [
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_ANALYTICS_INSIGHTS),
        element: <AnalyticsDashboard />,
        children: [
          {
            index: true,
            element: <InsightsPage />,
          },
          {
            path: dashboardRoutePath(
              ROUTES.DASHBOARD_ANALYTICS_INSIGHTS_ALL,
              ROUTES.DASHBOARD_ANALYTICS_INSIGHTS
            ),
            element: <AllInsightsPage />,
          },
        ],
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_ANALYTICS_TASK_LOGS),
        element: <AnalyticsDashboard />,
        children: [
          {
            index: true,
            element: <TaskLogsPage />,
          },
          {
            path: dashboardRoutePath(
              ROUTES.DASHBOARD_ANALYTICS_TASK_LOG_DETAILS(':taskId'),
              ROUTES.DASHBOARD_ANALYTICS_TASK_LOGS
            ),
            element: <TaskLogDetailsPage />,
          },
        ],
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_ANALYTICS_ASSIGNMENT_LOGS),
        element: <AnalyticsDashboard />,
        children: [
          {
            index: true,
            element: <AssignmentLogsPage />,
          },
          {
            path: dashboardRoutePath(
              ROUTES.DASHBOARD_ANALYTICS_ASSIGNMENT_LOG_DETAILS(':id'),
              ROUTES.DASHBOARD_ANALYTICS_ASSIGNMENT_LOGS
            ),
            element: <AssignmentLogDetailsPage />,
          },
        ],
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_SELECT_AGENT),
        element: <DashboardPage />,
      },
      {
        path: dashboardRoutePath(
          ROUTES.DASHBOARD_ANALYTICS_ACTIVATE_SUITE(':suiteId')
        ),
        element: <DashboardActivatePage />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_ANALYTICS_SCHEDULE_HEARTBEAT),
        element: <ScheduleHeartbeatPage />,
      },
      {
        path: dashboardRoutePath(
          ROUTES.DASHBOARD_ANALYTICS_SUITE_DETAILS(':suiteId')
        ),
        element: <AnalyticsSuiteDetailsPage />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_AI_AGENTS),
        element: <AIAgentsDashboardPage />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_AGENT_SUITE(':suiteId')),
        element: <AgentSuiteDetailPage />,
      },
      {
        path: dashboardRoutePath(
          ROUTES.DASHBOARD_AGENT_COMPANY_INFO(':suiteId')
        ),
        element: <CompanyInfoSettings />,
      },
      {
        path: dashboardRoutePath(
          ROUTES.DASHBOARD_AGENT_DEPARTMENT_INFO(':suiteId')
        ),
        element: <DepartmentInfoSettings />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_AGENT_ACTIVATION),
        element: <AgentActivationPage />,
      },
      {
        path: dashboardRoutePath(
          ROUTES.DASHBOARD_AGENT_ACTIVATION_SUITE(':suiteId')
        ),
        element: <AgentActivationPage />,
      },
      {
        path: dashboardRoutePath(
          ROUTES.DASHBOARD_AGENT_ACTIVATION_AGENT(':agentId')
        ),
        element: <AgentActivationPage />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_BUSINESS_STACK),
        element: <BusinessStackDashboardPage />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_BUSINESS_STACK_SELECT_AGENT),
        element: <BusinessStackSelectAgentPage />,
      },
      {
        path: dashboardRoutePath(
          ROUTES.DASHBOARD_BUSINESS_STACK_ACTIVATE_SUITE(':suiteId')
        ),
        element: <BusinessStackActivateSuitePage />,
      },
      {
        path: dashboardRoutePath(
          ROUTES.DASHBOARD_BUSINESS_STACK_SALESFORCE_HELP
        ),
        element: <SalesforceHelpPage />,
      },
      {
        path: dashboardRoutePath(
          ROUTES.DASHBOARD_BUSINESS_STACK_WITH_APP(':appKey')
        ),
        element: <BusinessStackDashboardPage />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_KNOWLEDGE_BASE),
        element: <KnowledgeBaseDashboardPage />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_KNOWLEDGE_BASE_SELECT_AGENT),
        element: <KnowledgeBaseSelectAgentPage />,
      },
      {
        path: dashboardRoutePath(
          ROUTES.DASHBOARD_KNOWLEDGE_BASE_ACTIVATE_SUITE(':suiteId')
        ),
        element: <KnowledgeBaseActivationPage />,
      },
      ...(isRouteAccessibleInCurrentEnvironment(ROUTES.DASHBOARD_POLICY_BOOKS)
        ? [
            {
              path: dashboardRoutePath(
                ROUTES.DASHBOARD_POLICY_BOOK_PENDING_APPROVAL
              ),
              element: <PolicyBookPendingApprovalPage />,
            },
            {
              path: dashboardRoutePath(
                ROUTES.DASHBOARD_POLICY_BOOK_AUDIT_LOG(':suiteKey')
              ),
              element: <PolicyBookAuditLogPage />,
            },
            {
              path: dashboardRoutePath(
                ROUTES.DASHBOARD_POLICY_BOOK_DRAFTS(':suiteKey')
              ),
              element: <PolicyBookDraftsPage />,
            },
            {
              path: dashboardRoutePath(
                ROUTES.DASHBOARD_POLICY_BOOK_COMMENTS(':suiteKey')
              ),
              element: <PolicyBookCommentsPage />,
            },
            {
              path: dashboardRoutePath(
                ROUTES.DASHBOARD_POLICY_BOOK_DETAILS(':suiteKey')
              ),
              element: <PolicyBookDetailsPage />,
            },
            {
              path: dashboardRoutePath(ROUTES.DASHBOARD_POLICY_BOOKS),
              element: <PolicyBooksPage />,
            },
          ]
        : [
            {
              path: dashboardRoutePath(ROUTES.DASHBOARD_POLICY_BOOKS),
              element: <Navigate to={ROUTES.DASHBOARD_AI_AGENTS} replace />,
            },
            {
              path: `${dashboardRoutePath(ROUTES.DASHBOARD_POLICY_BOOKS)}/*`,
              element: <Navigate to={ROUTES.DASHBOARD_AI_AGENTS} replace />,
            },
          ]),
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_AGENTIC_TICKER),
        element: <AgenticTickerPage />,
      },
      {
        path: `${dashboardRoutePath(ROUTES.DASHBOARD_SETTINGS)}/*`,
        element: <SettingsPage />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_MEMBERS),
        element: <MembersSettings />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_MEMBERS_INVITE),
        element: <InviteMembersPage />,
      },
      {
        path: dashboardRoutePath(ROUTES.DASHBOARD_MEMBERS_REQUEST_TO_JOIN),
        element: <RequestToJoinPage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];
